import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { apiClient, type ApiClientError } from '@/lib/api-client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/efs/PageHeader';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

const NS = 'portal';

interface BuyerProposalDto {
  buyer_proposal_id: string;
  monthly_batch_id: string;
  vendor_company_id: string;
  material_id: string | null;
  reason: string;
  estimated_unit_price: string | null;
  conditions: string | null;
  supplier_restriction: string | null;
  status: 'proposed' | 'approved' | 'rejected' | 'info_requested';
  decided_at: string | null;
  decision_note: string | null;
  created_at: string;
}

interface VendorOption {
  company_id: string;
  display_name: string;
  party_type: string;
}

const fetchProposals = async (batchId: string): Promise<BuyerProposalDto[]> => {
  const resp = await apiClient.get<{ data: BuyerProposalDto[] }>(
    `/monthly-batches/${batchId}/buyer-proposals`,
  );
  return resp.data.data;
};

const fetchCompanies = async (): Promise<VendorOption[]> => {
  const resp = await apiClient.get<{ data: VendorOption[] }>('/companies');
  return resp.data.data;
};

type DecideKind = 'approve' | 'reject' | 'request-info';

/**
 * FR-012 buyer proposals (minimal v1, Decision #3).
 *
 * The processor proposes a sales buyer with a reason and optional estimate;
 * the platform decides. Sales outbounds for this batch refuse vendors with no
 * APPROVED proposal — the page surfaces that linkage in its hint copy.
 */
export const BuyerProposalsPage = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const { t } = useTranslation([NS, 'common']);
  const user = useCurrentUser();
  const queryClient = useQueryClient();

  const canRead = hasPermission(user, 'sales.buyer_proposal.read');
  const canCreate = hasPermission(user, 'sales.buyer_proposal.create');
  const canDecide = hasPermission(user, 'sales.buyer_proposal.decide');

  const list = useQuery({
    queryKey: ['buyer-proposals', batchId],
    queryFn: () => fetchProposals(batchId!),
    enabled: !!batchId && (canRead || canCreate || canDecide),
  });
  const companies = useQuery({
    queryKey: ['companies', 'vendor-options'],
    queryFn: fetchCompanies,
    enabled: canCreate,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['buyer-proposals', batchId] });

  const [createOpen, setCreateOpen] = useState(false);
  const [vendorId, setVendorId] = useState('');
  const [reason, setReason] = useState('');
  const [estimate, setEstimate] = useState('');

  const create = useMutation({
    mutationFn: () =>
      apiClient.post(`/monthly-batches/${batchId}/buyer-proposals`, {
        vendor_company_id: vendorId,
        reason: reason.trim(),
        estimated_unit_price: estimate.trim() || null,
      }),
    onSuccess: () => {
      setCreateOpen(false);
      setVendorId('');
      setReason('');
      setEstimate('');
      void invalidate();
    },
  });

  const [decide, setDecide] = useState<{ kind: DecideKind; proposal: BuyerProposalDto } | null>(
    null,
  );
  const [decisionNote, setDecisionNote] = useState('');
  const [restriction, setRestriction] = useState('');

  const decideMut = useMutation({
    mutationFn: () =>
      apiClient.post(
        `/monthly-batches/${batchId}/buyer-proposals/${decide!.proposal.buyer_proposal_id}/${decide!.kind}`,
        {
          decision_note: decisionNote.trim() || null,
          supplier_restriction: restriction.trim() || null,
        },
      ),
    onSuccess: () => {
      setDecide(null);
      setDecisionNote('');
      setRestriction('');
      void invalidate();
    },
  });

  const vendorName = (id: string) =>
    companies.data?.find((c) => c.company_id === id)?.display_name ?? id.slice(0, 8);

  const rows = list.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.portal.proposals.title')}
        subtitle={t('efs.portal.proposals.subtitle')}
        count={rows.length}
      />

      {canCreate ? (
        <div>
          <Button onClick={() => setCreateOpen(true)}>{t('efs.portal.proposals.create')}</Button>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <p className="text-sm text-efs-text-muted">{t('efs.portal.proposals.empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('efs.portal.proposals.fields.vendor')}</TableHead>
              <TableHead>{t('efs.portal.proposals.fields.reason')}</TableHead>
              <TableHead>{t('efs.portal.proposals.fields.estimate')}</TableHead>
              <TableHead>{t('efs.portal.proposals.fields.status')}</TableHead>
              <TableHead className="text-right">
                {t('efs.portal.proposals.fields.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.buyer_proposal_id}>
                <TableCell className="font-medium">{vendorName(p.vendor_company_id)}</TableCell>
                <TableCell>
                  <span className="text-sm">{p.reason}</span>
                  {p.supplier_restriction ? (
                    <span className="block text-xs text-efs-text-muted">
                      {t('efs.portal.proposals.fields.restriction')}: {p.supplier_restriction}
                    </span>
                  ) : null}
                  {p.decision_note ? (
                    <span className="block text-xs text-efs-text-muted">
                      {t('efs.portal.proposals.fields.decision_note')}: {p.decision_note}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell>{p.estimated_unit_price ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant={p.status === 'approved' ? 'default' : 'outline'}>
                    {t(`efs.portal.proposals.statuses.${p.status}`)}
                  </Badge>
                </TableCell>
                <TableCell className="space-x-1 text-right">
                  {canDecide && (p.status === 'proposed' || p.status === 'info_requested') ? (
                    <>
                      <Button size="sm" onClick={() => setDecide({ kind: 'approve', proposal: p })}>
                        {t('efs.portal.proposals.approve')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDecide({ kind: 'reject', proposal: p })}
                      >
                        {t('efs.portal.proposals.reject')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDecide({ kind: 'request-info', proposal: p })}
                      >
                        {t('efs.portal.proposals.request_info')}
                      </Button>
                    </>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* propose */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('efs.portal.proposals.create')}</DialogTitle>
            <DialogDescription>{t('efs.portal.proposals.create_hint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t('efs.portal.proposals.fields.vendor')}</Label>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('efs.portal.proposals.vendor_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {(companies.data ?? []).map((c) => (
                    <SelectItem key={c.company_id} value={c.company_id}>
                      {c.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="bp-reason">{t('efs.portal.proposals.fields.reason')}</Label>
              <Textarea
                id="bp-reason"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="bp-estimate">{t('efs.portal.proposals.fields.estimate')}</Label>
              <Input
                id="bp-estimate"
                value={estimate}
                onChange={(e) => setEstimate(e.target.value)}
                placeholder="50.000"
              />
            </div>
            {create.isError ? (
              <p className="text-sm text-efs-danger">
                {(create.error as ApiClientError | undefined)?.message ??
                  t('efs.common.status.error', { ns: 'common' })}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={create.isPending}
            >
              {t('efs.common.actions.cancel', { ns: 'common' })}
            </Button>
            <Button
              type="button"
              onClick={() => create.mutate()}
              disabled={create.isPending || !vendorId || !reason.trim()}
            >
              {create.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              {t('efs.portal.proposals.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* decide */}
      <Dialog open={!!decide} onOpenChange={(v) => (!v ? setDecide(null) : undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decide ? t(`efs.portal.proposals.decide_titles.${decide.kind}`) : null}
            </DialogTitle>
            <DialogDescription>
              {decide ? vendorName(decide.proposal.vendor_company_id) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="bp-note">{t('efs.portal.proposals.fields.decision_note')}</Label>
              <Textarea
                id="bp-note"
                rows={3}
                value={decisionNote}
                onChange={(e) => setDecisionNote(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="bp-restriction">{t('efs.portal.proposals.fields.restriction')}</Label>
              <Input
                id="bp-restriction"
                value={restriction}
                onChange={(e) => setRestriction(e.target.value)}
                placeholder={t('efs.portal.proposals.restriction_placeholder')}
              />
            </div>
            {decideMut.isError ? (
              <p className="text-sm text-efs-danger">
                {(decideMut.error as ApiClientError | undefined)?.message ??
                  t('efs.common.status.error', { ns: 'common' })}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDecide(null)}
              disabled={decideMut.isPending}
            >
              {t('efs.common.actions.cancel', { ns: 'common' })}
            </Button>
            <Button type="button" onClick={() => decideMut.mutate()} disabled={decideMut.isPending}>
              {decideMut.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              {t('efs.portal.proposals.confirm_decision')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BuyerProposalsPage;
